import React from 'react';

/**
 * Match Explanation Component
 * Shows "Why This Match?" breakdown
 */
const MatchExplanation = ({ explanation, score, isExpanded, onToggle, aiMatchSource }) => {
  const hasExplanation = explanation && explanation.length > 0;
  const isAIMatch = aiMatchSource === 'ai_semantic' || (explanation || []).some(e => e.isAI);

  // Show at least the score toggle even without detailed explanation
  if (!hasExplanation && !score) return null;

  const getColorClass = (color) => {
    const colors = {
      green: 'match-factor-green',
      yellow: 'match-factor-yellow',
      blue: 'match-factor-blue',
      gray: 'match-factor-gray'
    };
    return colors[color] || colors.gray;
  };

  const getScoreColor = (s) => {
    if (s >= 80) return '#22c55e';
    if (s >= 60) return '#06b6d4';
    if (s >= 40) return '#eab308';
    return '#9ca3af';
  };

  const getBarColor = (s) => {
    if (s >= 70) return '#22c55e';
    if (s >= 40) return '#eab308';
    return '#9ca3af';
  };

  return (
    <div className="match-explanation" style={{ marginTop: '12px' }}>
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#06b6d4',
          fontWeight: 500,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0
        }}
      >
        <span style={{
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          display: 'inline-block'
        }}>&#9654;</span>
        <span>Why this match?</span>
        {isAIMatch && (
          <span style={{
            fontSize: '10px',
            fontWeight: 600,
            color: '#7c3aed',
            background: '#ede9fe',
            padding: '1px 6px',
            borderRadius: '4px',
            letterSpacing: '0.5px'
          }}>AI</span>
        )}
        <span style={{ fontWeight: 700, color: getScoreColor(score) }}>{score}%</span>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{
          marginTop: '12px',
          padding: '16px',
          background: 'linear-gradient(135deg, #f9fafb, #ffffff)',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <h4 style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {isAIMatch ? 'AI Match Analysis' : 'Match Analysis'}
            {isAIMatch && (
              <span style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#7c3aed',
                background: '#ede9fe',
                padding: '2px 8px',
                borderRadius: '4px'
              }}>Powered by AI</span>
            )}
          </h4>

          {!hasExplanation && (
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 8px' }}>
              Detailed factor breakdown not available for this match.
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(explanation || []).map((item, idx) => (
              <div
                key={idx}
                className={getColorClass(item.color)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: item.color === 'green' ? '#bbf7d0' : item.color === 'yellow' ? '#fef08a' : item.color === 'blue' ? '#bae6fd' : '#e5e7eb',
                  background: item.color === 'green' ? '#f0fdf4' : item.color === 'yellow' ? '#fefce8' : item.color === 'blue' ? '#f0f9ff' : '#f9fafb'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 500, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {item.factor}
                      {item.isAI && <span style={{ fontSize: '9px', fontWeight: 600, color: '#7c3aed', background: '#ede9fe', padding: '0 4px', borderRadius: '3px' }}>AI</span>}
                    </span>
                    <p style={{ fontSize: '11px', opacity: 0.8, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.text}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '8px' }}>
                  <div style={{ width: '64px', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        borderRadius: '4px',
                        transition: 'width 0.5s',
                        background: getBarColor(item.score),
                        width: `${item.score}%`
                      }}
                    />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 700, width: '32px', textAlign: 'right' }}>{item.score}%</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Powered by SkillDelivery AI</span>
            <span style={{ fontSize: '11px', color: isAIMatch ? '#7c3aed' : '#06b6d4', fontWeight: 500 }}>
              {isAIMatch ? 'Semantic AI + practitioner-trained' : 'Practitioner-trained matching'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchExplanation;
